import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'

// How many variant images we ship per base model. Keep in sync with the files
// under `public/img/outlet/<base-model>-<n>.png`.
const OUTLET_IMAGE_REGISTRY: Record<string, number> = {
  'iphone-12': 4,
  'iphone-13': 3,
  'iphone-14': 4,
  'iphone-15': 3,
  'macbook-air-m1': 4,
  'macbook-air-m2': 3,
  'macbook-air-m3': 3,
  'macbook-air-m4': 2,
  'macbook-pro-i5': 4,
  'macbook-pro-m1': 3,
  'macbook-pro-m2': 4,
  'macbook-pro-m3': 3,
  'macbook-pro-m4': 4,
}

const IPHONE_FALLBACK = 'iphone-15'
const MACBOOK_FALLBACK = 'macbook-pro-m4'

function detectBaseModel(
  product: Pick<AlgoliaProduct, 'title_model' | 'cat_id'>,
): string {
  const title = product.title_model?.toLowerCase() ?? ''

  const iphoneMatch = title.match(/iphone\s*(12|13|14|15)/)
  if (iphoneMatch) return `iphone-${iphoneMatch[1]}`

  if (title.includes('macbook')) {
    const family = title.includes('air') ? 'air' : 'pro'
    const chipMatch = title.match(/\b(m1|m2|m3|m4)\b/)
    if (chipMatch) return `macbook-${family}-${chipMatch[1]}`
    if (title.includes('intel') || title.includes('i5')) {
      return 'macbook-pro-i5'
    }

    return `macbook-${family}-${family === 'air' ? 'm1' : 'm4'}`
  }

  // No identifiable model — pick a fallback based on what category we know
  // the product belongs to.
  if (product.cat_id === 2) return IPHONE_FALLBACK

  return MACBOOK_FALLBACK
}

export function getOutletProductFamily(
  product: Pick<AlgoliaProduct, 'title_model' | 'cat_id'> | null | undefined,
): 'iphone' | 'macbook' {
  if (!product) return 'iphone'

  return detectBaseModel(product).startsWith('iphone') ? 'iphone' : 'macbook'
}

// Returns the public URL of the first static image for the given hit. Used on
// the listing, the home preview, and the main image of the detail page.
export function getOutletProductImage(
  product: Pick<AlgoliaProduct, 'title_model' | 'cat_id'> | null | undefined,
): string {
  const baseModel = product ? detectBaseModel(product) : IPHONE_FALLBACK

  return `/img/outlet/${baseModel}-1.png`
}

// Returns every available image for the given hit, in order. Used by the
// product-detail gallery modal so the user can swipe through all variants.
export function getOutletProductGalleryImages(
  product: Pick<AlgoliaProduct, 'title_model' | 'cat_id'> | null | undefined,
): string[] {
  if (!product) return [`/img/outlet/${IPHONE_FALLBACK}-1.png`]

  const baseModel = detectBaseModel(product)
  const variantCount = OUTLET_IMAGE_REGISTRY[baseModel] ?? 1

  return Array.from(
    { length: variantCount },
    (_, i) => `/img/outlet/${baseModel}-${i + 1}.png`,
  )
}
